import { z } from 'zod';

// Authentication Validation Schemas
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(8, "Password must be at least 8 characters"),
  rememberMe: z.boolean(),
});

export const registerSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
  // teamName: z
  //   .string()
  //   .min(1, 'Team name is required')
  //   .max(100, 'Team name must be less than 100 characters'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

export const resetPasswordSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Project Validation Schemas
export const projectSchema = z.object({
  title: z
    .string()
    .min(1, 'Project title is required')
    .max(255, 'Title must be less than 255 characters'),
  description: z
    .string()
    .max(2000, 'Description must be less than 2000 characters')
    .optional(),
  deadline: z
    .date()
    .min(new Date(), 'Deadline must be in the future')
    .optional(),
  priority: z.enum(['low', 'medium', 'high']),
  assignedMembers: z.array(z.string().uuid()).optional(),
});

// Content Validation Schemas
export const contentSchema = z.object({
  title: z
    .string()
    .min(1, 'Content title is required')
    .max(255, 'Title must be less than 255 characters'),
  content: z
    .string()
    .min(1, 'Content body is required'),
  contentType: z.enum(['blog', 'social', 'video', 'email', 'script']),
  scheduledPublishAt: z.date().optional(),
  tags: z.array(z.string()).optional(),
});

// Team Invitation Schema
export const teamInviteSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  role: z.enum(['admin', 'editor', 'viewer', 'analyst']),
  message: z
    .string()
    .max(500, 'Message must be less than 500 characters')
    .optional(),
});

// Profile Update Schema
export const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

// Helper Types
export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
export type ProjectFormData = z.infer<typeof projectSchema>;
export type ContentFormData = z.infer<typeof contentSchema>;
export type TeamInviteFormData = z.infer<typeof teamInviteSchema>;
export type ProfileFormData = z.infer<typeof profileSchema>;

// Validation Helper Functions
export const validateField = <T>(schema: z.ZodSchema<T>, data: any, field: keyof T) => {
  try {
    schema.parse(data);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldError = error.errors.find(err => err.path.includes(field as string));
      return fieldError?.message || null;
    }
    return null;
  }
};

export const validateForm = <T>(schema: z.ZodSchema<T>, data: any) => {
  try {
    schema.parse(data);
    return { isValid: true, errors: [] };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        isValid: false,
        errors: error.errors.map(err => ({
          field: err.path.join('.'),
          message: err.message,
        })),
      };
    }
    return { isValid: false, errors: [{ field: 'general', message: 'Validation failed' }] };
  }
};